<template>
    <div class="mt-5 mb-5">
        <b-alert show v-if="!canCreate" variant="warning">
            <div class="d-flex align-items-center">
                <b-icon icon="info-circle" class="mr-2"/>
                <span>Создание мероприятий по выбытию невозможно, т.к. животное в данный момент не находится на содержании или содержится в другой организации.</span>
            </div>
        </b-alert>
        <div class="mb-3">
            <b-link v-if="canCreateReleaseRequest" :to="{ name: 'requestCreate', query: linkQuery }">
                <b-icon icon="plus" class="mr-1"/>
                <span>Создать заявку на выпуск</span>
            </b-link>
        </div>
        <data-table ref="events"
                    :api-route="backendApiRoute"
                    :api-view-set="table.apiViewSet"
                    :name="table.name"
                    :columns="table.columns"
                    :search="table.search"
                    :filters="table.filters ? table.filters : []"
                    :initial-filters="initialFilters"
                    :actions="table.actions"
                    :orderings="table.orderings"
                    :initial-orderings="table.initialOrderings"
                    :row-actions="table.rowActions"
                    :key="table.key"
                    :destroy-title="'Удаление мероприятия'"
                    :context-menu-wrapper-id="''">
        </data-table>
    </div>
</template>

<script>
import DataTable from "@/components/Table/DataTable";
import {mapGetters} from "vuex";
import api from "@/api";
import {eventFactory, EventKind, EventKindLabel} from "@/pages/AppPages/Events/event";
import actions, {Action, AvailableAction} from "@/components/Table/actions";
import {ACTIVITIES} from "@/store/auth/auth";
import store from "@/store";
import httpClient from "@/http";

export default {
    name: "DisposalEventList",
    components: { DataTable },
    props: {
        animal: { type: Object, default: () => { return {} } },
    },
    data() {
        const name = 'disposal-events-table';
        let columns = [];

        columns.push({ name: 'id', 'title': 'Номер',
            to: {
                idColumn: 'id',
                routeName: 'eventDetail',
                prependWithN: true
            },
            alwaysVisible: true,
            extraDataFrom: 'kind_name'
        });

        columns.push(
            { name: 'request_object', 'title': 'Заявка на отлов',
                cellComponentName: 'RequestNumberStatusCell',
                cellComponentPath: 'src/pages/DataTableCells/'},
            { name: 'release_request_object', 'title': 'Заявка на выпуск',
                cellComponentName: 'RequestNumberStatusCell',
                cellComponentPath: 'src/pages/DataTableCells/'},
            { name: 'executor_contractor_name', 'title': 'Организация'},
            { name: 'executor_name', 'title': 'Провел'},
            { name: 'execution_date', 'title': 'Проведено',
                    cellComponentName: 'DateTimeCell',
                    cellComponentPath: 'src/pages/DataTableCells/'},
            { name: 'created_at', 'title': 'Создано',
                    cellComponentName: 'DateTimeCell',
                    cellComponentPath: 'src/pages/DataTableCells/'},
        );

        return {
            table: {
                name: name,
                apiViewSet: api.events.default,
                key: name,
                search: true,
                columns: columns,
                orderings: [
                    'id',
                    { 'created_at_date_only': 'created_at' },
                    { 'execution_date_only': 'execution_date' },
                ],
                actions: [],
                rowActions: [],
                filters: [],
                initialFilters: {},
                initialOrderings: { id: 2 }
            },
            lastCatchingRequestId: null
        }
    },
    computed: {
        ACTIVITIES() {
            return ACTIVITIES
        },
        ...mapGetters(['ability', 'userOrganizationId', 'isInContractor', 'isInCustomer', 'userOrganizationActivities', 'isSuperuser']),
        backendApiRoute() {
            return api.events.default.list()
        },
        initialFilters() {
            return { 'kind': [EventKind.RETURN_TO_HABITAT, EventKind.RETURN_TO_OWNER, EventKind.DEATH, EventKind.EUTHANASIA, EventKind.TRANSFER], 'animal': this.animal.id };
        },
        disposalEventKinds() {
            return [EventKind.RETURN_TO_OWNER, EventKind.EUTHANASIA,
                EventKind.DEATH, EventKind.TRANSFER]
        },
        canCreateReleaseRequest() {
            return this.isSuperuser || this.isInCustomer
                && this.userOrganizationActivities.includes(ACTIVITIES.KEEPING)
                && this.animal.keepingPlaceId === this.userOrganizationId  && this.animal.isKept
        },
        linkQuery() {
            let query = { activity: ACTIVITIES.RELEASE };
            if (this.animal) {
                query['animal'] = this.animal.id
            }

            return query
        },
        canCreate() {
            return (this.ability.can('create', 'DisposalEvent') && this.animal.isKept && this.animal.keepingPlaceId === this.userOrganizationId) || this.isSuperuser;
        }
    },
    mounted() {
        const vm = this;
        const canCreate = vm.canCreate;
        if (canCreate) {
            let createAction = null;
            let dropdownList = [];

            for (let i = 0; i < this.disposalEventKinds.length; i++) {
                const kind = this.disposalEventKinds[i];
                const text = EventKindLabel[kind][0];
                const route = { name: 'eventCreate', params: { animalId: vm.animal.id, kind: kind } };
                dropdownList.push({text: text, route: route })
            }

            createAction = new Action({
                name: 'createWithContext',
                icon: 'plus',
                class: 'btn-primary',
                title: 'Добавить',
                showTitle: false,
                dropdownList: dropdownList,
                emittedEvent: 'on-create',
                available: canCreate
            }, (choice) => {
                vm.$router.push(choice.route)
            });
            this.table.actions.push(createAction);
        }

        if (this.ability.can('destroy', 'DisposalEvent')) {
            const destroyAction = new AvailableAction({ title: 'Удалить', icon: 'trash', emittedEvent: 'on-delete' }, (id, row) => {
                const objectClass = eventFactory(row['kind']);
                const object = new objectClass();
                object.applyData(row);
                return object
            }, (row) => {
                const objectClass = new eventFactory(row['kind']);
                const object = new objectClass();
                object.applyData(row);
                return store.getters.ability.can('destroy', object);
            })
            this.table.rowActions.push(destroyAction);
        }

        // if (this.canCreateReleaseRequest && this.animal.lastCatchingId) {
        //     httpClient.get(api.events.default.detail(this.animal.lastCatchingId))
        //         .then((response) => {
        //             vm.lastCatchingRequestId = response.data['request'];
        //         }).catch(() => {
        //
        //         })
        // }
    }

}
</script>

<style scoped>

</style>